import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const saveBlob = (content, type, filename) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ModelBadge = ({ cfg }) => (
  <p data-testid="remuneration-model" className="text-sm text-zinc-300">
    Modèle de rémunération actif :{' '}
    {cfg.model === 'mutualise'
      ? <b className="text-[#FFD60A]">mutualisé à partir du {new Date(cfg.cutover_at).toLocaleString('fr-FR')}</b>
      : <b className="text-zinc-100">historique (legacy) — le calcul mutualisé tourne en observation</b>}
  </p>
);

const AuditSummary = ({ audit }) => (
  <div data-testid="audit-summary" className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
    {Object.entries(audit.summary).map(([k, v]) => (
      <div key={k} className="bg-zinc-900 border border-zinc-800 rounded p-2">
        <p className="text-zinc-500">{k.replace(/_/g, ' ')}</p>
        <p className="text-white font-semibold">{String(v)}</p>
      </div>
    ))}
  </div>
);

const StatementRow = ({ s }) => {
  const csv = async () => {
    const r = await axios.get(`${API}/api/admin/remuneration/statements/${s.driver_id}/${s.month}/csv`, { headers: authHeaders() });
    saveBlob(r.data, 'text/csv', `releve_${s.month}_${s.driver_id.slice(0, 8)}.csv`);
  };
  return (
    <div data-testid={`statement-row-${s.id}`} className="flex items-center justify-between text-sm bg-zinc-900 border border-zinc-800 rounded p-2">
      <span>{s.month} · {s.driver_name} · {Number(s.total_km).toFixed(2)} km · {Number(s.total_revenue).toFixed(2)} € · {s.payout_status}
        {s.pending_review_count ? ` · ${s.pending_review_count} tronçon(s) en vérification` : ''}</span>
      <Button size="sm" variant="outline" onClick={csv} data-testid={`statement-csv-btn-${s.id}`}><FileSpreadsheet className="w-4 h-4 mr-1" /> CSV</Button>
    </div>
  );
};

export const RemunerationAuditPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [audit, setAudit] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/remuneration/config`, { headers: authHeaders() }).then((r) => setCfg(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/remuneration/statements`, { headers: authHeaders() }).then((r) => setStatements(r.data.statements)).catch(() => {});
  }, []);

  const runAudit = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/remuneration/audit`, { headers: authHeaders() });
      setAudit(r.data);
    } catch (e) {
      toast.error("Audit impossible");
    } finally {
      setLoading(false);
    }
  };

  if (!cfg) return null;
  return (
    <div data-testid="remuneration-audit-panel" className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4 mb-6 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FFD60A]" /> Rémunération mutualisée</h2>
        <div className="flex gap-2">
          <Button size="sm" onClick={runAudit} disabled={loading} data-testid="run-audit-btn">Lancer l'audit (lecture seule)</Button>
          {audit && (
            <Button size="sm" variant="outline" data-testid="download-audit-btn"
              onClick={() => saveBlob(JSON.stringify(audit, null, 2), 'application/json', `audit_remuneration_${audit.generated_at.slice(0, 10)}.json`)}>
              <Download className="w-4 h-4 mr-1" /> Rapport JSON
            </Button>
          )}
        </div>
      </div>
      <ModelBadge cfg={cfg} />
      {audit && <AuditSummary audit={audit} />}
      {statements.map((s) => <StatementRow key={s.id} s={s} />)}
    </div>
  );
};
